/** The fields a job is searched by. */
interface SearchableJob {
  title: string
  location?: string | null
  company?: string | null
  referenceNumber?: string | null
}

/** Lead forms and Meta campaigns are only searched by name. */
interface SearchableNamed {
  name: string
}

/**
 * ATS `location` arrives as a comma-joined "city, region, country" with empty
 * segments where the provider had no value ("Berlin, , Germany"), so the blanks
 * are dropped rather than rendered as stray commas.
 */
export const formatJobLocation = (location?: string | null): string =>
  (location || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .join(', ')

/** Lowercase and drop diacritics: "München" -> "munchen". */
const stripDiacritics = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

/** Lowercase and expand umlauts the German way: "München" -> "muenchen". */
const expandUmlauts = (value: string): string =>
  value
    .toLowerCase()
    .replace(/\u00df/g, 'ss')
    .replace(/\u00e4/g, 'ae')
    .replace(/\u00f6/g, 'oe')
    .replace(/\u00fc/g, 'ue')

/**
 * Both transliterations are kept side by side, because German names are full of
 * umlauts and nobody reliably types them when searching — "muenchen", "munchen"
 * and "münchen" all have to find "München", and no single normalisation covers
 * both ASCII spellings at once.
 */
const buildHaystack = (parts: (string | null | undefined)[]): string => {
  const raw = parts.filter(Boolean).join(' ')
  return `${stripDiacritics(raw)} ${expandUmlauts(raw)}`
}

/**
 * Matches every token in `query` somewhere in the text built by `toParts`, so
 * "sales berlin" and "berlin sales" both find the Berlin sales role. MUI's
 * default filter only does a substring match on the option label, so word order
 * and umlaut spelling have to match exactly.
 *
 * `_`, `|`, `/` and `-` count as separators in the query: Meta campaign names are
 * often written like "Pflege_Berlin | 2026", and the haystack keeps those
 * characters, so each token still matches on its own.
 */
const filterByQuery = <T>(
  items: T[],
  query: string,
  toParts: (item: T) => (string | null | undefined)[]
): T[] => {
  // Queries are only diacritic-stripped; an umlaut typed as "ü" folds to "u",
  // which the haystack's stripDiacritics half still matches.
  const tokens = stripDiacritics(query)
    .split(/[\s_|/-]+/)
    .filter(Boolean)
  if (!tokens.length) return items

  return items.filter((item) => {
    const haystack = buildHaystack(toParts(item))
    return tokens.every((token) => haystack.includes(token))
  })
}

/**
 * Jobs match on title plus everything that distinguishes two jobs sharing a
 * title word (location, company, reference number).
 */
export const filterJobs = <T extends SearchableJob>(jobs: T[], query: string): T[] =>
  filterByQuery(jobs, query, (job) => [
    job.title,
    formatJobLocation(job.location),
    job.company,
    job.referenceNumber,
  ])

/** Lead forms and original Meta campaigns only carry a name to search on. */
export const filterCampaigns = <T extends SearchableNamed>(campaigns: T[], query: string): T[] =>
  filterByQuery(campaigns, query, (campaign) => [campaign.name])
